import React, { useEffect, useState } from 'react';
import { Callout, Icon, Button } from '../../../components/ui';

export interface AiError {
  status?: number;
  code?: string;
  retry_after_seconds?: number;
  message?: string;
}

interface ErrorBannerProps {
  error: AiError | null;
  onDismiss?: () => void;
}

function parseAiError(err: AiError): {
  title: string;
  text: string;
  retryAfter?: number;
} {
  const { status, code, retry_after_seconds, message } = err;

  if (status === 401) return { title: 'Authentication required', text: 'Please sign in to continue' };
  if (status === 403 && code === 'ai_disabled') return { title: 'Feature unavailable', text: 'Feature is currently disabled for your account' };
  if (status === 429) {
    if (code === 'ai_budget_exceeded') return { title: 'Usage cap reached', text: 'Daily AI usage cap reached. Resets at 00:00 UTC.' };
    if (code === 'ai_global_cap_exceeded') return { title: 'Service unavailable', text: 'AI service is temporarily unavailable. Please try again soon.' };
    if (code === 'ai_provider_unavailable') return { title: 'AI temporarily unavailable', text: 'AI temporarily unavailable, retrying in {n}s', retryAfter: retry_after_seconds ?? 60 };
    if (code === 'rate_limited') return { title: 'Too many requests', text: 'Too many requests. Slow down.' };
  }
  if (status === 409 && code === 'conversation_advanced') return { title: 'Conversation moved on', text: 'Conversation has moved on. Please refresh to continue.' };
  if (status === 400 && code === 'client_turn_id_expired') return { title: 'Message expired', text: 'Your message expired. Please retry.' };
  if (status === 503 && code === 'busy') return { title: 'Processing request', text: 'Processing previous request — one moment...' };

  return { title: 'Error', text: message ?? 'An error occurred. Please try again.' };
}

const ErrorBanner: React.FC<ErrorBannerProps> = ({ error, onDismiss }) => {
  const [countdown, setCountdown] = useState<number | null>(null);

  useEffect(() => {
    if (!error) { setCountdown(null); return; }
    const parsed = parseAiError(error);
    if (parsed.retryAfter != null) setCountdown(parsed.retryAfter);
  }, [error]);

  useEffect(() => {
    if (countdown === null || countdown <= 0) return;
    const id = setInterval(() => {
      setCountdown((c) => {
        if (c === null || c <= 1) { clearInterval(id); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [countdown]);

  if (!error) return null;

  const parsed = parseAiError(error);
  let displayText = parsed.text;
  if (parsed.retryAfter != null && countdown !== null) {
    displayText = displayText.replace('{n}', String(countdown));
  }

  return (
    <Callout tone="warn" style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <Icon name="alertCircle" size={16} style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        <strong style={{ display: 'block', marginBottom: 2 }}>{parsed.title}</strong>
        <span style={{ fontSize: 13 }}>{displayText}</span>
      </div>
      {onDismiss && (
        <Button variant="ghost" size="icon" onClick={onDismiss} aria-label="dismiss error" style={{ flexShrink: 0 }}>
          <Icon name="x" size={14} />
        </Button>
      )}
    </Callout>
  );
};

export default ErrorBanner;
